import { Button } from "@/components/ui/button";
import { useTerritory } from "@/contexts/TerritoryContext";
import { Check, Sparkles } from "lucide-react";

export default function Pricing() {
  const { territory } = useTerritory();

  const base = [
    `All ${territory.examName} subjects: Maths, English, Science & Social Studies`,
    "Unlimited practice questions & quizzes",
    "Gamified learning: XP, levels & streaks",
    "AI-powered personalised recommendations",
    "Real-time progress tracking & performance reports",
    "Leaderboard & friendly competition",
    "Monthly prize campaigns with GiftMe smart cards",
    "Works on any device: phone, tablet or computer",
  ];
  const pricingPlans = [
    { name: "Monthly", price: territory.monthlyPrice, currency: territory.currency, period: "/month", description: "Full access, month to month", features: base, popular: false, savings: "" },
    { name: "Annual", price: territory.yearlyPrice, currency: territory.currency, period: "/year", description: "Everything in Monthly, plus", features: [...base, "Weekly WhatsApp progress reports for parents", "Priority support"], popular: true, savings: territory.yearlySavings },
  ];

  return (
    <section id="pricing" className="py-20 md:py-28 bg-primary/5">
      <div className="container px-4 md:px-6 max-w-7xl mx-auto">
        <div className="text-center space-y-3 mb-12">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Pricing</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="text-pricing-headline">Simple, affordable pricing</h2>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto" data-testid="text-pricing-description">Pick the plan that suits your family. Cancel any time.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-5 md:gap-6 max-w-4xl mx-auto items-start">
          {pricingPlans.map((plan, index) => (
            <div key={index} className={`relative rounded-2xl bg-card border p-6 sm:p-8 ${plan.popular ? "border-primary ring-2 ring-primary/30 shadow-md" : "border-border shadow-sm"}`} data-testid={`card-pricing-${plan.name.toLowerCase()}`}>
              {plan.popular && (
                <span className="absolute -top-3 left-6 inline-flex items-center gap-1 text-xs font-bold px-3 py-1 rounded-full bg-primary text-primary-foreground shadow-sm"><Sparkles className="w-3.5 h-3.5" /><span data-testid="badge-most-popular">Most popular</span></span>
              )}
              <h3 className="text-xl font-bold" data-testid={`text-plan-name-${index}`}>{plan.name}</h3>
              <p className="text-sm text-muted-foreground mt-1" data-testid={`text-plan-description-${index}`}>{plan.description}</p>
              <div className="mt-4 flex items-baseline gap-1.5">
                <span className={`text-4xl sm:text-5xl font-bold tabular-nums tracking-tight ${plan.popular ? "text-primary" : ""}`} data-testid={`text-plan-price-${index}`}>{plan.price}</span>
                <span className={`text-lg font-bold ${plan.popular ? "text-primary" : ""}`} data-testid={`text-plan-currency-${index}`}>{plan.currency}</span>
                <span className="text-muted-foreground text-sm" data-testid={`text-plan-period-${index}`}>{plan.period}</span>
              </div>
              {plan.savings && (
                <span className="mt-2 inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-500 text-white" data-testid={`text-plan-savings-${index}`}>{plan.savings}</span>
              )}
              <ul className="mt-6 space-y-3">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-3 text-sm sm:text-base" data-testid={`feature-${index}-${featureIndex}`}>
                    <span className={`mt-0.5 w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${plan.popular ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}><Check className="w-3 h-3" strokeWidth={3} /></span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <Button size="lg" variant={plan.popular ? "default" : "outline"} className={`mt-8 w-full h-12 text-base rounded-xl font-semibold ${plan.popular ? "" : "border-primary text-primary hover:bg-primary hover:text-primary-foreground"}`} data-testid={`button-${plan.name.toLowerCase()}-plan`} onClick={() => window.location.href = '/signup'}>
                Get started
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
